import {
  CardElement as StripeCardElementComponent,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type {
  CreatePaymentMethodData,
  StripeCardElement,
  StripeCardElementChangeEvent,
} from '@stripe/stripe-js';
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import {
  deletePaymentMethod,
  getPaymentMethods,
  type PaymentMethod,
} from './apiHelpers';

export const usePaymentMethods = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentMethodRequestPending, setIsPaymentMethodRequestPending] =
    useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [name, setName] = useState('');
  const [setupFutureCardUsage, setSetupFutureCardUsage] = useState(true);
  const [isCardElementFormComplete, setIsCardElementFormComplete] =
    useState(false);
  const [checkoutResponseError, setCheckoutResponseError] = useState<
    string | undefined
  >();

  const loadPaymentMethods = async (isFirst: boolean) => {
    setIsLoading(true);
    try {
      const response = await getPaymentMethods();
      setPaymentMethods(response);
      if (isFirst) setSelectedPaymentMethodId(response?.[0]?.id);
    } catch (error) {
      errorToast({
        message:
          'An error occurred loading your payment methods. Please try again.',
      });
      reportAppError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadPaymentMethods(true);
  }, []);

  const isNewPaymentFormComplete = !!name && isCardElementFormComplete;
  const canCheckOut = selectedPaymentMethodId || isNewPaymentFormComplete;
  const isPayButtonDisabled = isPaymentMethodRequestPending || !canCheckOut;

  const handleOnChangeName = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setCheckoutResponseError(undefined);
    setName(value);
  };

  const handleOnChangeCardElement = ({
    complete,
  }: StripeCardElementChangeEvent) => {
    if (complete) {
      setIsCardElementFormComplete(complete);
    }
    setCheckoutResponseError(undefined);
  };

  const processPaymentMethod = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!stripe || !elements) {
      return undefined;
    }

    setIsPaymentMethodRequestPending(true);
    let stripeResponse;
    if (!selectedPaymentMethodId) {
      stripeResponse = await stripe.createPaymentMethod({
        billing_details: {
          name,
        },
        card: elements.getElement(
          StripeCardElementComponent,
        ) as StripeCardElement,
        type: 'card',
        allow_redisplay: setupFutureCardUsage ? 'always' : 'limited',
      } as CreatePaymentMethodData);

      if (stripeResponse.error) {
        setIsPaymentMethodRequestPending(false);
        setCheckoutResponseError(stripeResponse.error.message);
        return undefined;
      }
    }

    return stripeResponse;
  };

  const handleOnError = (message: string) => {
    setIsPaymentMethodRequestPending(false);
    setCheckoutResponseError(message);
  };

  const handleOnChangePaymentMethod = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setSelectedPaymentMethodId(value);
    setCheckoutResponseError(undefined);
  };

  const handleOnChangeSaveCard = () => {
    setSetupFutureCardUsage(!setupFutureCardUsage);
  };

  const handleOnDeletePaymentMethod = (id: string) => {
    void (async () => {
      await deletePaymentMethod(id);
      void loadPaymentMethods(false);
    })();
  };

  return {
    checkoutResponseError,
    handleOnChangeCardElement,
    handleOnChangeName,
    handleOnChangePaymentMethod,
    handleOnChangeSaveCard,
    handleOnDeletePaymentMethod,
    handleOnError,
    isLoading,
    isPayButtonDisabled,
    paymentMethods,
    processPaymentMethod,
    selectedPaymentMethodId,
    setupFutureCardUsage,
  };
};
